import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { MEMBERSHIP_TIERS } from "@/lib/constants";
import { createCheckoutSession, postMembership, formatApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, ArrowUpRight, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/lib/seo";

export default function Membership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("professional");
  const [form, setForm] = useState({ name: "", email: "", organisation: "", country: "", motivation: "" });
  const [submitting, setSubmitting] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const tier = MEMBERSHIP_TIERS.find((m) => m.code === selected);
  const isPaid = ["student", "professional", "corporate", "institutional"].includes(selected);

  const startCheckout = async () => {
    if (!user) {
      toast.info("Please sign in to subscribe.");
      navigate(`/login?next=${encodeURIComponent("/membership")}`);
      return;
    }
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const { data } = await createCheckoutSession(selected, origin);
      if (data?.url) window.location.href = data.url;
      else throw new Error("Missing checkout url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setCheckingOut(false);
    }
  };

  const startSubscription = async () => {
    if (!user) {
      toast.info("Please sign in to subscribe.");
      navigate(`/login?next=${encodeURIComponent("/membership")}`);
      return;
    }
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/subscription/checkout", { tier: selected, origin_url: origin });
      if (data?.url) window.location.href = data.url;
      else throw new Error("Missing checkout url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start subscription");
      setCheckingOut(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postMembership({ ...form, tier: selected });
      toast.success("Application received. The Institute will review and respond.");
      setForm({ name: "", email: "", organisation: "", country: "", motivation: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="membership-page">
      <SEO title="Membership · The Cleanability Institute" description="Join the Institute through one of five membership tiers." path="/membership" />
      <PageHero
        testId="membership-hero"
        eyebrow="Membership"
        title="Join the people writing the discipline."
        lead="Researchers, practitioners, designers, operators and institutions converge at the Institute through five membership tiers. Each tier opens different doors into the work."
      />

      <section className="container-x section-y border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {MEMBERSHIP_TIERS.map((t, i) => (
            <Reveal key={t.code} delay={i * 0.05}>
              <button
                type="button"
                onClick={() => setSelected(t.code)}
                data-testid={`tier-${t.code}`}
                className={`text-left rounded-2xl border bg-card p-6 hover-lift h-full w-full transition-colors ${
                  selected === t.code
                    ? "border-[hsl(var(--accent))] ring-emerald"
                    : "border-border hover:border-foreground/40"
                }`}
              >
                <div className="font-mono-mini text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 font-display text-xl font-medium tracking-tight">{t.name}</div>
                <div className="mt-1 text-xs text-[hsl(var(--accent))] font-medium">{t.price}</div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{t.description}</p>
                <ul className="mt-6 space-y-2">
                  {t.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-foreground/85">
                      <Check className="h-3.5 w-3.5 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>{isPaid ? "Subscribe" : "Apply"}</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                {isPaid ? `Subscribe to the ${tier.name} tier.` : `Apply for the ${tier.name} tier.`}
              </h2>
              <p className="mt-6 text-muted-foreground">
                {isPaid
                  ? "Pay securely with Stripe. Your access activates the moment payment confirms."
                  : "Fellowship is by invitation. Tell us about your work — the Institute will be in touch."}
              </p>
              <ul className="mt-10 space-y-3 text-sm">
                {tier.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
              {user && (
                <div className="mt-8 text-xs text-muted-foreground">
                  Signed in as <span className="text-foreground font-medium">{user.email}</span> · current tier <span className="text-foreground font-medium">{user.tier || "free"}</span>
                </div>
              )}
            </div>

            <div className="lg:col-span-7">
              {isPaid ? (
                <div data-testid="membership-checkout" className="rounded-3xl border border-border bg-card p-8 md:p-10">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="font-mono-mini text-muted-foreground">Selected tier</div>
                      <div className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight">{tier.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-mini text-muted-foreground">Annual</div>
                      <div className="mt-2 font-display text-3xl md:text-4xl font-medium tracking-tight text-[hsl(var(--accent))]">{tier.price}</div>
                    </div>
                  </div>
                  <ul className="mt-8 space-y-2.5">
                    {tier.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> {b}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={startCheckout}
                    disabled={checkingOut}
                    data-testid="checkout-button"
                    className="mt-10 w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60"
                  >
                    {checkingOut ? "Redirecting…" : <>One-time annual payment <CreditCard className="h-4 w-4" /></>}
                  </button>
                  <button
                    onClick={startSubscription}
                    disabled={checkingOut}
                    data-testid="subscription-button"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))] disabled:opacity-60"
                  >
                    {checkingOut ? "Redirecting…" : <>Auto-renewing subscription</>}
                  </button>
                  <div className="mt-4 text-[11px] text-muted-foreground text-center">
                    Secure payment by Stripe. GBP currency. Cancel anytime.
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="membership-form" className="rounded-3xl border border-border bg-card p-8 md:p-10 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInput label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="member-name" />
                    <FormInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="member-email" />
                    <FormInput label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testId="member-org" />
                    <FormInput label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="member-country" />
                  </div>
                  <div>
                    <label className="text-xs font-mono-mini text-muted-foreground">Why this matters to you</label>
                    <textarea
                      data-testid="member-motivation"
                      value={form.motivation}
                      onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                      rows={4}
                      className="mt-2 w-full px-4 py-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="member-submit"
                    className="inline-flex items-center gap-2 px-6 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? "Submitting…" : "Submit application"} <Mail className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
